import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { getThemeMode } from '../utils/theme';
import { appConfig } from '../utils/appConfig';
import { getAccessToken } from '../actions/user';
import { loginRequest } from '../authConfig';
import wordmarkBlack from '../assets/altronic-wordmark-black.svg';
import wordmarkWhite from '../assets/altronic-wordmark-white.svg';
import '../scss/auth.scss';

function Authentication() {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const isDark = getThemeMode() === 'dark';
    const wordmark = isDark ? wordmarkWhite : wordmarkBlack;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!password || loading) return;
        setLoading(true);
        setError(null);
        try {
            await getAccessToken(password);
            const isAdmin = sessionStorage.getItem('is_admin') === '1';
            if (isAdmin) {
                try {
                    await instance.loginPopup(loginRequest);
                } catch (msalErr) {
                    sessionStorage.removeItem('token');
                    sessionStorage.removeItem('is_admin');
                    setError('Microsoft sign-in is required for admin access.');
                    setLoading(false);
                    return;
                }
            }
            navigate('/chat', { replace: true });
        } catch {
            setError('Incorrect access key. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div className="authentication-wrapper">
            <main className="main-section">
                <img className="logo" src={wordmark} alt="Altronic" />

                <div className="auth-form-container">
                    <span className="eyebrow auth-eyebrow">Secure Access</span>
                    <h3 className="auth-heading">Enter your access key</h3>
                    <p className="auth-sub">
                        Contact your system administrator if you need access.
                    </p>

                    <form className="auth-form" onSubmit={handleSubmit} noValidate>
                        <div className={`auth-input-wrap${error ? ' auth-input-wrap--error' : ''}`}>
                            <input
                                className="auth-input"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Access key"
                                value={password}
                                autoFocus
                                autoComplete="current-password"
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (error) setError(null);
                                }}
                            />
                            <button
                                type="button"
                                className="auth-toggle-btn"
                                onClick={() => setShowPassword(v => !v)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>

                        {error && <p className="auth-error-msg">{error}</p>}

                        <button
                            type="submit"
                            className="auth-submit-btn"
                            disabled={!password || loading}
                        >
                            {loading ? <span className="auth-spinner" /> : 'Continue'}
                        </button>
                    </form>
                </div>
            </main>

            <div className="bg-img3" />
            {/* <div className="bg" /> */}
            <span className="version">v. {appConfig.version}</span>
        </div>
    );
}

function EyeIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}

function EyeOffIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    );
}

export default Authentication;
